/* ============================================
   PDP FLEX VARIANT SYNC
   --------------------------------------------
   Keeps the "PDP Variant Flex Content" section (feature spec rows, spec
   tables, etc.) in sync with the selected variant WITHOUT a page reload.

   How it works:
   The theme's variant-picker (assets/variant-picker.js) already fetches the
   FULL product page HTML for the newly-selected variant on every change — it
   just discards everything except the variant-picker element. That response is
   handed to listeners on the `variant:update` event as `detail.data.html`
   (a parsed Document). Since the flex section renders server-side from the
   variant's `custom.flex_content` metafield, the correct new markup is already
   sitting in that document. We lift it out and swap it in.

   No extra network request. No page refresh.
   ============================================ */

(function () {
  const SECTION_SELECTOR = '.pdp-variant-flex';

  /**
   * Wake up the block-level scripts for freshly-injected markup.
   *
   * Two flavours of block script live in this theme:
   *   1. Scripts that listen for `shopify:section:load` and read `event.target`
   *      (pdp-data-table.js, reviews-slider.js, video-reviews-slider.js).
   *      Dispatching the event ON THE SECTION ELEMENT — the way Shopify itself
   *      does — scopes their re-query to the new markup only. Dispatching on
   *      `document` instead would make `event.target` the document and cause
   *      them to re-init every matching slider on the page.
   *   2. Scripts that only bootstrap on DOMContentLoaded and expose their class
   *      on `window` (faq.js, pdp-reviews.js). Those we re-construct directly.
   *      Their constructors are document-scoped and self-locating, so a fresh
   *      instance simply re-binds against the new DOM.
   *
   * Sliders without a `shopify:section:load` hook and without a window export
   * (cards / gallery / collection / suggested) cannot be re-initialised from
   * here — see the note in the section Liquid.
   *
   * @param {Element} sectionEl - The newly-injected flex section.
   */
  function reinitBlocks(sectionEl) {
    const shopifySection = sectionEl.closest('[id^="shopify-section-"]');
    const sectionId = shopifySection ? shopifySection.id.replace('shopify-section-', '') : '';

    // Flavour 1 — target the section element so `event.target` is correct.
    sectionEl.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId },
      })
    );

    // Flavour 2 — re-construct the DOMContentLoaded-only components.
    if (sectionEl.querySelector('#FAQ') && typeof window.FAQAccordion === 'function') {
      new window.FAQAccordion();
    }
    if (sectionEl.querySelector('.pdp-reviews-list') && typeof window.PDPReviews === 'function') {
      new window.PDPReviews();
    }
  }

  /**
   * Swap every flex section on the page with its counterpart from the new HTML.
   * @param {Document} newDoc - Parsed document for the newly-selected variant.
   */
  function syncFlexSections(newDoc) {
    const currentSections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (!currentSections.length) return;

    const newSections = Array.from(newDoc.querySelectorAll(SECTION_SELECTOR));
    if (!newSections.length) return;

    const singlePair = currentSections.length === 1 && newSections.length === 1;

    currentSections.forEach((current) => {
      // Match on the section id stamped onto the wrapper, so a template with
      // more than one flex section gets each one replaced correctly. Fall back
      // to positional matching only when there is exactly one of each.
      const sectionId = current.dataset.sectionId;
      const replacement =
        (sectionId && newSections.find((s) => s.dataset.sectionId === sectionId)) ||
        (singlePair ? newSections[0] : null);

      if (!replacement) return;

      // Identical content between the two variants — skip the DOM churn and
      // the re-init entirely. Avoids flicker when an option change doesn't
      // affect the flex content.
      if (replacement.innerHTML === current.innerHTML) return;

      const imported = document.importNode(replacement, true);
      current.replaceWith(imported);
      reinitBlocks(imported);
    });
  }

  document.addEventListener('variant:update', (event) => {
    const newDoc = event?.detail?.data?.html;
    if (!newDoc) return;

    try {
      syncFlexSections(newDoc);
    } catch (error) {
      // Never let a sync failure break the rest of the variant update.
      console.error('[pdp-flex-variant-sync] Failed to sync flex content:', error);
    }
  });
})();
